// VAT Returns Lifecycle UI · Phase 1
//
// One screen, five tabs — Prepare / Review / File / History / Audit.
// Backed by the Fix #13 lifecycle engine (services/vat_return_engine.py)
// and Audit Stream (routers/audit_stream.py). No business logic here.
//
//   Prepare → preview snapshot, then save it as 'prepared'
//   Review  → walk the saved snapshot + drill into sales / purchases
//   File    → flip prepared → filed (immutable, confirmation gated)
//   History → every persisted return (any status), with deep links
//   Audit   → VAT_RETURN_PREPARED + VAT_RETURN_FILED stream
//
// The screen anchors on a *period* (YYYY-MM). Selecting a period
// auto-loads the saved return (if any) and the preview snapshot in
// parallel.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Calculator, FileCheck2, FileText, Lock, History, ShieldCheck, RefreshCw,
  Loader2, AlertTriangle, CheckCircle2, ArrowDownToLine, Download,
  TrendingUp, TrendingDown, Building2, Send, Eye,
  ChevronRight, ChevronDown, FileSearch,
} from 'lucide-react';
import VATDesyncSection from '../components/VATDesyncSection';

const VAT_RATE_LABEL = 'Standard rate · 15%';
const STATUS_BADGE = {
  prepared: { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Prepared' },
  filed:    { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Filed' },
  draft:    { color: 'bg-slate-100 text-slate-700 border-slate-200', label: 'Draft' },
};

const fmtSAR = (v) => {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

// Builds the list of selectable periods. We fetch the cadence
// (monthly vs quarterly) from the backend on first render so the
// dropdown matches the company's configured ZATCA filing frequency.
// Falls back to "quarterly" with a 2-year window if the endpoint is
// unreachable — best default for KSA contractors (≤ 40M SAR turnover).
function fallbackQuarterlyOptions() {
  const opts = [];
  const today = new Date();
  let y = today.getFullYear();
  let q = Math.floor(today.getMonth() / 3) + 1;
  for (let i = 0; i < 8; i++) {
    opts.push({ name: `${y}-Q${q}`, label: `Q${q} ${y}` });
    q -= 1;
    if (q === 0) { q = 4; y -= 1; }
  }
  return opts;
}


export default function VATReturns() {
  // Cadence is fetched on mount. Until it loads, we render a quarterly
  // fallback so the UI never blocks; once the real cadence arrives we
  // swap the options and switch to the server's recommended default
  // period (typically the most recently closed quarter/month).
  const [cadence, setCadence] = useState({
    frequency: 'quarterly',
    options: fallbackQuarterlyOptions(),
    default_period: null,
  });
  const periodOptions = cadence.options;
  const [periodName, setPeriodName] = useState(
    () => fallbackQuarterlyOptions()[1]?.name || fallbackQuarterlyOptions()[0]?.name
  );

  // Fetch the real cadence from the company profile.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get('/vat/cadence');
        if (cancelled) return;
        setCadence(data);
        if (data?.default_period) setPeriodName(data.default_period);
      } catch {
        /* keep quarterly fallback */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const [tab, setTab] = useState('prepare');

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [saved, setSaved] = useState(null);          // saved return for the active period
  const [history, setHistory] = useState([]);        // every persisted return
  const [historyLoading, setHistoryLoading] = useState(false);

  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [preparing, setPreparing] = useState(false);
  const [filing, setFiling] = useState(false);
  const [confirmFile, setConfirmFile] = useState(false);

  // ─── Loaders ───────────────────────────────────────────────────────
  const loadPreview = useCallback(async (pn) => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const { data } = await axiosInstance.get(`/vat/returns/preview/${pn}`);
      setPreview(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPreview(null);
      setPreviewError(
        detail?.error === 'period_not_found'
          ? `Period ${pn} is not defined in the accounting periods.`
          : err.response?.data?.detail?.toString?.() || err.message,
      );
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axiosInstance.get('/vat/returns');
      const rows = data?.returns || [];
      setHistory(rows);
      // The saved entry for the active period (if any).
      const match = rows.find((r) => r.period_name === periodName);
      setSaved(match || null);
    } catch (err) {
      const serverMsg = err.response?.data?.detail || err.response?.data?.error
                     || err.response?.statusText || err.message;
      toast.error(`Failed to load VAT returns history: ${serverMsg}`);
      // Keep history empty but not stale.
      setHistory([]);
      setSaved(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [periodName]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data } = await axiosInstance.get(
        '/audit/stream?tags=VAT_RETURN_PREPARED,VAT_RETURN_FILED&limit=50',
      );
      setAudit(data?.events || []);
    } catch (err) {
      toast.error('Failed to load VAT audit stream');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  // Parallel load on period change.
  useEffect(() => {
    if (!periodName) return;
    let active = true;
    const handle = setTimeout(() => {
      if (!active) return;
      loadPreview(periodName);
      loadHistory();
      loadAudit();
    }, 0);
    return () => { active = false; clearTimeout(handle); };
  }, [periodName, loadPreview, loadHistory, loadAudit]);

  // ─── Actions ───────────────────────────────────────────────────────
  const handlePrepare = async () => {
    setPreparing(true);
    try {
      const { data } = await axiosInstance.post(`/vat/returns/${periodName}/prepare`);
      setSaved(data);
      toast.success(`VAT return prepared for ${periodName}`);
      loadHistory();
      loadAudit();
      setTab('review');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.error(typeof detail === 'string' ? detail : 'Return already filed — cannot re-prepare');
      } else if (status === 404) {
        toast.error('Period not found in accounting calendar');
      } else if (status === 403) {
        toast.error('Permission denied: vat_return.prepare');
      } else {
        toast.error('Failed to prepare VAT return');
      }
    } finally {
      setPreparing(false);
    }
  };

  const handleFile = async () => {
    if (!saved?.id) return;
    setFiling(true);
    try {
      const { data } = await axiosInstance.post(`/vat/returns/${saved.id}/file`);
      setSaved(data);
      setConfirmFile(false);
      toast.success(`VAT return filed for ${periodName}. Immutable from now on.`);
      loadHistory();
      loadAudit();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        toast.error(typeof detail === 'string' ? detail : 'Conflict — already filed');
      } else if (status === 403) {
        toast.error('Permission denied: vat_return.file');
      } else {
        toast.error('Failed to file VAT return');
      }
    } finally {
      setFiling(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div
      data-testid="vat-returns-page"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4 sm:p-6 space-y-5"
      dir="ltr"
    >
      <PageHeader
        periodName={periodName}
        setPeriodName={setPeriodName}
        periodOptions={periodOptions}
        cadence={cadence}
        saved={saved}
        onRefresh={() => { loadPreview(periodName); loadHistory(); loadAudit(); }}
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-5 max-w-2xl">
          <TabsTrigger value="prepare" data-testid="tab-prepare">
            <Calculator size={14} className="mr-1.5" /> Prepare
          </TabsTrigger>
          <TabsTrigger value="review" data-testid="tab-review">
            <Eye size={14} className="mr-1.5" /> Review
          </TabsTrigger>
          <TabsTrigger value="file" data-testid="tab-file">
            <Send size={14} className="mr-1.5" /> File
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History size={14} className="mr-1.5" /> History
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <ShieldCheck size={14} className="mr-1.5" /> Audit
          </TabsTrigger>
        </TabsList>

        <TabsContent value="prepare" className="space-y-4">
          <PrepareTab
            periodName={periodName}
            preview={preview}
            loading={previewLoading}
            error={previewError}
            saved={saved}
            preparing={preparing}
            onPrepare={handlePrepare}
            onReloadPreview={() => loadPreview(periodName)}
          />
        </TabsContent>

        <TabsContent value="review" className="space-y-4">
          <ReviewTab
            saved={saved}
            preview={preview}
            periodName={periodName}
            onPrepareAgain={() => setTab('prepare')}
            onGoToFile={() => setTab('file')}
            onReloadPreview={() => loadPreview(periodName)}
          />
        </TabsContent>

        <TabsContent value="file" className="space-y-4">
          <FileTab
            saved={saved}
            onOpenConfirm={() => setConfirmFile(true)}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <HistoryTab
            rows={history}
            loading={historyLoading}
            onJump={(pn) => { setPeriodName(pn); setTab('review'); }}
          />
        </TabsContent>

        <TabsContent value="audit" className="space-y-4">
          <AuditTab events={audit} loading={auditLoading} />
        </TabsContent>
      </Tabs>

      <ConfirmFileDialog
        open={confirmFile}
        saved={saved}
        filing={filing}
        onCancel={() => setConfirmFile(false)}
        onConfirm={handleFile}
      />
    </div>
  );
}


// ─── Header ──────────────────────────────────────────────────────────────


function PageHeader({ periodName, setPeriodName, periodOptions, cadence, saved, onRefresh }) {
  const status = saved?.status || 'draft';
  const badge = STATUS_BADGE[status] || STATUS_BADGE.draft;
  const isQuarterly = cadence?.frequency === 'quarterly';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm">
            <FileCheck2 size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="vat-page-title">
              VAT Return Lifecycle
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Prepare, review, file and audit ZATCA-compatible VAT returns in one workspace.
            </p>
            <p className="text-[11px] text-slate-400 mt-1" data-testid="vat-cadence-label">
              {isQuarterly
                ? '📅 الدورة: ربع سنوية (Quarterly) — كل 3 أشهر بعد نهاية الربع'
                : '📅 الدورة: شهرية (Monthly) — كل شهر'}
              {' · '}
              <a href="/settings/company-profile" className="underline hover:text-amber-700">تغيير الإعداد</a>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            data-testid="period-select"
            value={periodName}
            onChange={(e) => setPeriodName(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-800 focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
          >
            {periodOptions.map((p) => (
              <option key={p.name} value={p.name}>{p.label} · {p.name}</option>
            ))}
          </select>
          <span
            data-testid="vat-status-badge"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${badge.color}`}
          >
            {badge.label}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            data-testid="vat-refresh-btn"
            className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:shadow-sm transition"
            title="Refresh"
          >
            <RefreshCw size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ─── Prepare Tab ─────────────────────────────────────────────────────────


function PrepareTab({ periodName, preview, loading, error, saved, preparing, onPrepare, onReloadPreview }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Computing VAT snapshot for {periodName}…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-rose-700 flex items-start gap-3" data-testid="prepare-error">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <div className="font-semibold mb-1">Cannot compute VAT for {periodName}</div>
            <div className="text-sm text-rose-600">{error}</div>
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!preview) return null;

  const filed = saved?.status === 'filed';
  const out = preview.output_vat || {};
  const inp = preview.input_vat || {};

  return (
    <div className="space-y-4" data-testid="prepare-content">
      {/* Action banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
          <Calculator size={16} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-slate-900">Computed snapshot for {periodName}</div>
          <div className="text-sm text-slate-600 mt-0.5">
            {filed
              ? 'This return has already been filed. Re-preparation is locked.'
              : saved?.status === 'prepared'
                ? 'A prepared snapshot already exists. Re-preparing overwrites it.'
                : 'No saved return yet — preview is live from current data.'}
          </div>
        </div>
        <Button
          data-testid="prepare-btn"
          disabled={filed || preparing}
          onClick={onPrepare}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {preparing
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Preparing…</>
            : saved
              ? <><RefreshCw className="w-4 h-4 mr-1.5" /> Re-prepare</>
              : <><FileCheck2 className="w-4 h-4 mr-1.5" /> Prepare Return</>}
        </Button>
      </div>

      <NetVATCard preview={preview} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          icon={TrendingUp}
          title="Output VAT · Sales"
          color="emerald"
          rows={[
            { label: 'Standard-rate base',     value: fmtSAR(out.standard_base) },
            { label: VAT_RATE_LABEL,           value: fmtSAR(out.standard_vat), highlight: true },
            { label: 'Zero-rated sales',       value: fmtSAR(out.zero_rated) },
            { label: 'Exempt sales',           value: fmtSAR(out.exempt) },
            { label: 'Invoices counted',       value: `${out.invoice_count || 0}` },
          ]}
        />
        <SectionCard
          icon={TrendingDown}
          title="Input VAT · Purchases & Expenses"
          color="indigo"
          rows={[
            { label: 'Standard-rate base',     value: fmtSAR(inp.standard_base) },
            { label: 'Reclaimable VAT',        value: fmtSAR(inp.standard_vat), highlight: true },
            { label: 'Supplier bills counted', value: `${inp.bills_count || 0}` },
            { label: 'Expenses counted',       value: `${inp.expenses_count || 0}` },
          ]}
        />
      </div>

      {/* Feb 2026 · Direct bridge to GL account 11300 so the accountant
          can cross-check Reclaimable VAT vs the live account balance
          BEFORE filing. */}
      <Account11300ReconcileCard
        periodStart={preview.period_start || preview.start_date}
        periodEnd={preview.period_end || preview.end_date}
        reclaimableVat={inp.standard_vat}
      />

      {/* Line-level table with claim-lifecycle markers (P-B.7.6). */}
      <PurchasesDetail
        purchases={preview.details?.purchases || []}
        periodName={periodName}
        filed={filed}
        onChanged={onReloadPreview}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// PurchasesDetail — surfaces every supplier bill / expense that fed
// (or was filtered from) the current return. Owner directive
// 2026-07-02: every entry must carry a marker showing whether it
// will be transferred to VAT for the current quarter; entries that
// won't be must be one click away from deferring to the next.
// ─────────────────────────────────────────────────────────────────────
function PurchasesDetail({ purchases, periodName, filed, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const nextPeriodName = useMemo(() => {
    // 2026-Q1 → 2026-Q2 · 2026-Q4 → 2027-Q1 · 2026-01 → 2026-02 · …
    const qm = periodName.match(/^(\d{4})-Q([1-4])$/);
    if (qm) {
      const y = Number(qm[1]);
      const q = Number(qm[2]);
      return q === 4 ? `${y + 1}-Q1` : `${y}-Q${q + 1}`;
    }
    const mm = periodName.match(/^(\d{4})-(\d{2})$/);
    if (mm) {
      const y = Number(mm[1]);
      const m = Number(mm[2]);
      return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
    }
    return periodName;
  }, [periodName]);

  // Hooks MUST come before any early return.
  // Feb 2026 · rollover fix — deferred bills targeting THIS period are
  // now automatically included by the backend (see vat_return_engine
  // `_aggregate_input_vat`). No manual bulk-reclaim step is needed;
  // the previous "⚡ Add all deferred" button caused data loss by
  // overwriting the deferral target and is intentionally removed.
  //
  // We keep a compact "Restore deferrals" recovery action in case the
  // user's history already contains an accidental bulk-reclaim event.
  const [restoreBusy, setRestoreBusy] = React.useState(false);
  const [undoBusy, setUndoBusy] = React.useState(false);
  const restoreDeferrals = async () => {
    if (!window.confirm(
      'استعادة كل الفواتير التي تم إلغاء تأجيلها بالخطأ خلال الفترات الأخيرة؟\n'
      + 'Restore all bills whose deferral was accidentally reverted?')) return;
    setRestoreBusy(true);
    try {
      const { data } = await axiosInstance.post('/vat-claims/undo-bulk-reclaim');
      const c = data?.counts || {};
      toast.success(
        `تمت الاستعادة: ${c.restored || 0} · تم تخطيها: ${c.skipped || 0}`);
      onChanged && onChanged();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Restore failed');
    } finally {
      setRestoreBusy(false);
    }
  };
  const undoRestore = async () => {
    if (!window.confirm(
      'التراجع عن آخر عملية "استعادة تأجيلات" وإعادة الفواتير إلى Q1 (claimable)?\n'
      + 'Undo the last restore and put those bills back to their natural period?')) return;
    setUndoBusy(true);
    try {
      const { data } = await axiosInstance.post('/vat-claims/undo-restore');
      const c = data?.counts || {};
      toast.success(
        `تم التراجع: ${c.restored || 0} · أخطاء: ${c.errors || 0}`);
      onChanged && onChanged();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Undo restore failed');
    } finally {
      setUndoBusy(false);
    }
  };

  if (!purchases || purchases.length === 0) return null;

  const defer = async (row) => {
    const dtype = row.source === 'supplier_bill' ? 'supplier_bill'
                : row.source === 'expense_report' ? 'expense_report'
                : row.source === 'bank_adjustment' ? 'bank_adjustment'
                : null;
    if (!dtype) {
      toast.info('This entry type cannot be deferred yet.');
      return;
    }
    setBusyId(row.id);
    try {
      await axiosInstance.post('/vat-claims/defer', {
        document_type: dtype,
        document_ids:  [row.id],
        to_period:     nextPeriodName,
      });
      toast.success(`${row.bill_number} moved to ${nextPeriodName}`);
      onChanged && onChanged();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to defer');
    } finally {
      setBusyId(null);
    }
  };

  const reclaim = async (row) => {
    const dtype = row.source === 'supplier_bill' ? 'supplier_bill'
                : row.source === 'expense_report' ? 'expense_report'
                : row.source === 'bank_adjustment' ? 'bank_adjustment'
                : null;
    if (!dtype) return;
    setBusyId(row.id);
    try {
      await axiosInstance.post('/vat-claims/reclaim', {
        document_type: dtype,
        document_ids:  [row.id],
      });
      toast.success(`${row.bill_number} added to ${periodName}`);
      onChanged && onChanged();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to reclaim');
    } finally {
      setBusyId(null);
    }
  };

  /** Feb 2026 · The former "⚡ إضافة كل المؤجَّلة" bulk-reclaim button
   *  is deliberately removed. Deferred bills whose target period
   *  matches the currently displayed return are now automatically
   *  included by the backend (see vat_return_engine._aggregate_input_vat).
   *  Manual reclamation is available per-row when the user genuinely
   *  wants to pull a deferral BACK to its source quarter. */

  return (
    <Card data-testid="purchases-detail">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText size={16} className="text-indigo-600" />
            Purchases in this return
          </CardTitle>
          {!filed && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={undoRestore}
                disabled={undoBusy}
                data-testid="undo-restore-btn"
                className="h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                title="التراجع عن آخر عملية استعادة (إعادة الفواتير إلى Q1)"
              >
                {undoBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : null}
                تراجع عن الاستعادة
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={restoreDeferrals}
                disabled={restoreBusy}
                data-testid="restore-deferrals-btn"
                className="h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                title="استعادة أي تأجيلات تم عكسها بالخطأ (audit-log replay)"
              >
                {restoreBusy
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                  : null}
                استعادة التأجيلات (إن وُجدت)
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left  py-2 pr-3 font-semibold">Bill / Ref</th>
                <th className="text-left  py-2 pr-3 font-semibold">Date</th>
                <th className="text-left  py-2 pr-3 font-semibold">Supplier</th>
                <th className="text-right py-2 pr-3 font-semibold">Base</th>
                <th className="text-right py-2 pr-3 font-semibold">VAT</th>
                <th className="text-center py-2 pr-3 font-semibold">Status</th>
                <th className="text-right py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((row) => {
                const included = row.included_in_return !== false;
                const status = row.vat_claim_status || 'claimable';
                const badge =
                  included
                    ? { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                        label: `Will file · ${periodName}` }
                    : status === 'deferred'
                      ? { cls: 'bg-amber-50 text-amber-700 border-amber-200',
                          label: `Deferred → ${row.vat_claim_period || 'later'}` }
                      : row.vat_claim_period && row.vat_claim_period !== periodName
                        ? { cls: 'bg-sky-50 text-sky-700 border-sky-200',
                            label: `Claimed → ${row.vat_claim_period}` }
                        : { cls: 'bg-slate-50 text-slate-600 border-slate-200',
                            label: status };
                return (
                  <tr key={row.id} className="border-b border-slate-100 last:border-0"
                      data-testid={`purchase-row-${row.bill_number}`}>
                    <td className="py-2 pr-3 font-medium text-slate-900">{row.bill_number || '—'}</td>
                    <td className="py-2 pr-3 text-slate-600">{row.date}</td>
                    <td className="py-2 pr-3 text-slate-700">{row.supplier_name || '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{fmtSAR(row.subtotal)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium">{fmtSAR(row.vat_amount)}</td>
                    <td className="py-2 pr-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${badge.cls}`}
                            data-testid={`purchase-status-${row.bill_number}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      {!filed && (
                        included ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === row.id}
                            onClick={() => defer(row)}
                            data-testid={`defer-btn-${row.bill_number}`}
                            className="h-7 px-2 text-xs"
                          >
                            {busyId === row.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <>Defer → {nextPeriodName}</>}
                          </Button>
                        ) : status === 'deferred' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === row.id}
                            onClick={() => reclaim(row)}
                            data-testid={`reclaim-btn-${row.bill_number}`}
                            className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            {busyId === row.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <>Add to {periodName}</>}
                          </Button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


function NetVATCard({ preview }) {
  if (!preview) return null;
  const direction = preview.direction;
  const value = preview.net_vat_due;
  const isPay = direction === 'pay';
  const isRefund = direction === 'refund';

  const accent = isPay
    ? { bg: 'bg-rose-50',     dot: 'bg-rose-500',    text: 'text-rose-700',    label: 'Payable to ZATCA' }
    : isRefund
      ? { bg: 'bg-emerald-50', dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Refund expected' }
      : { bg: 'bg-slate-50',   dot: 'bg-slate-400',   text: 'text-slate-600',   label: 'Net balanced' };

  return (
    <div
      data-testid="net-vat-card"
      className={`rounded-2xl border border-slate-200 ${accent.bg} p-5`}
    >
      <div className="flex items-center gap-3">
        <span className={`w-3 h-3 rounded-full ${accent.dot} shadow-sm`} />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Net VAT for {preview.period_name}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-baseline gap-3">
        <div className={`text-3xl font-bold tracking-tight ${accent.text}`}>
          {fmtSAR(Math.abs(value || 0))}
        </div>
        <div className={`text-sm font-semibold ${accent.text}`}>{accent.label}</div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">Output VAT</div>
          <div className="font-semibold text-slate-900">{fmtSAR(preview.output_vat?.standard_vat)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Input VAT</div>
          <div className="font-semibold text-slate-900">{fmtSAR(preview.input_vat?.standard_vat)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Period window</div>
          <div className="font-semibold text-slate-900 text-xs">
            {preview.start_date} → {preview.end_date}
          </div>
        </div>
      </div>
    </div>
  );
}


function SectionCard({ icon: Icon, title, color, rows }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-600',
    indigo:  'bg-indigo-50  text-indigo-600',
    rose:    'bg-rose-50    text-rose-600',
    amber:   'bg-amber-50   text-amber-600',
  };
  return (
    <Card className="overflow-hidden border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className={`p-1.5 rounded ${colorMap[color] || colorMap.indigo}`}>
            <Icon size={14} />
          </span>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="text-sm divide-y divide-slate-100">
          {rows.map((r, i) => (
            <li key={i} className="flex justify-between items-center py-2">
              <span className="text-slate-600">{r.label}</span>
              <span className={r.highlight ? 'font-bold text-slate-900' : 'font-medium text-slate-800'}>
                {r.value}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}


// ─── Account 11300 Reconcile Card (Feb 2026) ───────────────────────
// Bridges the "VAT Return" screen to the underlying GL account
// (11300 VAT Input · Receivable). During Prepare/Review the
// accountant can now cross-check the return's Reclaimable VAT vs the
// live GL account balance IN THE SAME SCREEN — no more tab-hopping.
// If the two numbers diverge the card flags the delta and links to
// the Reconciliation Center's Completeness Audit for a drill-down.
function Account11300ReconcileCard({ periodStart, periodEnd, reclaimableVat }) {
  const [loading, setLoading] = useState(false);
  const [audit, setAudit]     = useState(null);
  const [error, setError]     = useState(null);

  const run = async () => {
    if (!periodStart || !periodEnd) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axiosInstance.get(
        `/reports/vat-input-completeness?start_date=${periodStart}&end_date=${periodEnd}`);
      setAudit(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load 11300 snapshot');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { run(); }, [periodStart, periodEnd]);

  const glPosted = Number(audit?.summary?.posted_vat || 0);
  const gap      = Number(reclaimableVat || 0) - glPosted;
  const status   = Math.abs(gap) < 0.5 ? 'ok'
                   : Math.abs(gap) / Math.max(1, Math.abs(reclaimableVat || 1)) < 0.05
                     ? 'warn' : 'bad';
  const cls      = status === 'ok'   ? 'bg-emerald-50 border-emerald-200'
                   : status === 'warn' ? 'bg-amber-50 border-amber-200'
                   : 'bg-rose-50 border-rose-200';

  const fmt = (n) => Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={`rounded-lg border p-4 ${cls}`}
         data-testid="account-11300-reconcile-card">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
            🔗 حساب 11300 · VAT Input (Receivable) — تدقيق مباشر
          </div>
          <p className="text-[11px] text-slate-600 mt-0.5">
            مقارنة &quot;Reclaimable VAT&quot; في هذا الإقرار مع فعلياً ما هو
            مُقيَّد على حساب 11300 خلال نفس الفترة. للتحقق قبل التقديم أو
            قبل التسليم للمدقّق.
          </p>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={run} disabled={loading}
                  data-testid="btn-refresh-11300">
            {loading ? <Loader2 size={12} className="animate-spin" /> : '↻'}
            تحديث
          </Button>
          <Link to="/reconciliation-center" data-testid="btn-open-recon-center">
            <Button size="sm" variant="outline">
              فتح مركز التسوية
            </Button>
          </Link>
        </div>
      </div>
      {error && <div className="text-[11px] text-rose-700">{error}</div>}
      {!error && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">Return · Reclaimable</div>
            <div className="font-mono font-bold text-sm" data-testid="reclaim-11300-return">
              {fmt(reclaimableVat)}
            </div>
          </div>
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">GL · Posted to 11300</div>
            <div className="font-mono font-bold text-sm text-emerald-700"
                 data-testid="reclaim-11300-gl">
              {fmt(glPosted)}
            </div>
          </div>
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">Δ Gap</div>
            <div className={`font-mono font-bold text-sm ${
                status === 'ok' ? 'text-emerald-700'
                : status === 'warn' ? 'text-amber-700' : 'text-rose-700'}`}
                 data-testid="reclaim-11300-gap">
              {fmt(gap)}
            </div>
          </div>
          <div className="bg-white rounded p-2 border border-slate-200">
            <div className="text-[10px] uppercase text-slate-500">Missing docs</div>
            <div className="font-mono font-bold text-sm text-rose-700"
                 data-testid="reclaim-11300-missing">
              {audit?.summary?.missing_doc_count || 0}
              {audit?.summary?.missing_group_count > 0 && (
                <span className="text-[10px] text-slate-500 ml-1">
                  ({audit.summary.missing_group_count} groups)
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {status !== 'ok' && audit && (
        <p className={`text-[11px] mt-2 ${status === 'warn' ? 'text-amber-800' : 'text-rose-800'} italic`}>
          {status === 'warn'
            ? 'الفرق طفيف — ربما بسبب قيود متأخرة (posted_date خارج نافذة الإقرار). راجع بانل الـ Completeness Audit في مركز التسوية.'
            : 'الفرق كبير — قد تكون هناك مستندات ينقصها سطر VAT_INPUT. افتح مركز التسوية للتفاصيل.'}
        </p>
      )}
      {status === 'ok' && audit && (
        <p className="text-[11px] mt-2 text-emerald-800 italic">
          ✓ رصيد حساب 11300 خلال هذه الفترة يطابق قيمة Reclaimable VAT في الإقرار — جاهز للمدقّق.
        </p>
      )}
    </div>
  );
}


// ─── Review Tab ──────────────────────────────────────────────────────────


function ReviewTab({ saved, preview, periodName, onPrepareAgain, onGoToFile, onReloadPreview }) {
  if (!saved) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500" data-testid="review-empty">
          <FileSearch className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <div className="text-sm">No saved return for this period yet.</div>
          <Button
            variant="outline"
            className="mt-3"
            onClick={onPrepareAgain}
            data-testid="review-go-prepare"
          >
            Go to Prepare
          </Button>
        </CardContent>
      </Card>
    );
  }

  const filed = saved.status === 'filed';

  return (
    <div className="space-y-4" data-testid="review-content">
      <NetVATCard preview={{ ...saved, output_vat: saved.output_vat, input_vat: saved.input_vat }} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Eye size={16} className="text-indigo-600" />
              Snapshot details
            </span>
            <span className="text-xs font-normal text-slate-500">
              Prepared {fmtTime(saved.prepared_at)} by {saved.prepared_by_name}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard
              icon={TrendingUp}
              title="Output VAT · Sales"
              color="emerald"
              rows={[
                { label: 'Standard-rate base', value: fmtSAR(saved.output_vat?.standard_base) },
                { label: VAT_RATE_LABEL, value: fmtSAR(saved.output_vat?.standard_vat), highlight: true },
                { label: 'Zero-rated', value: fmtSAR(saved.output_vat?.zero_rated) },
                { label: 'Invoices', value: `${saved.output_vat?.invoice_count || 0}` },
              ]}
            />
            <SectionCard
              icon={TrendingDown}
              title="Input VAT · Purchases"
              color="indigo"
              rows={[
                { label: 'Standard-rate base', value: fmtSAR(saved.input_vat?.standard_base) },
                { label: 'Reclaimable VAT', value: fmtSAR(saved.input_vat?.standard_vat), highlight: true },
                { label: 'Bills counted', value: `${saved.input_vat?.bills_count || 0}` },
                { label: 'Expenses counted', value: `${saved.input_vat?.expenses_count || 0}` },
              ]}
            />
          </div>

          <Account11300ReconcileCard
            periodStart={saved.period_start || saved.start_date}
            periodEnd={saved.period_end || saved.end_date}
            reclaimableVat={saved.input_vat?.standard_vat}
          />

          {preview?.details?.sales?.length > 0 && (
            <DetailTable
              testid="review-sales-table"
              title={`Sales source documents · ${preview.details.sales.length}`}
              columns={[
                { key: 'invoice_number', label: 'Invoice' },
                { key: 'date',           label: 'Date' },
                { key: 'client_name',    label: 'Client' },
                { key: 'subtotal',       label: 'Base', format: 'currency' },
                { key: 'vat_amount',     label: 'VAT', format: 'currency', emphasis: true },
                { key: 'total',          label: 'Total', format: 'currency' },
              ]}
              rows={preview.details.sales}
            />
          )}

          {preview?.details?.purchases?.length > 0 && (
            <PurchasesDetail
              purchases={preview.details.purchases}
              periodName={periodName}
              filed={filed}
              onChanged={onReloadPreview}
            />
          )}

          {/* STA-5: Sub-Ledger ↔ GL VAT desync surface — anchored on
             the loaded return so it is independent of the Reconciliation
             Center's active period. */}
          <VATDesyncSection returnId={saved.id} filed={filed} />

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="text-xs text-slate-500">
              {filed
                ? <span className="text-emerald-700 font-semibold">Filed {fmtTime(saved.filed_at)} · immutable</span>
                : 'Re-prepare to refresh the numbers, or proceed to File.'}
            </div>
            <div className="flex gap-2">
              {!filed && (
                <Button
                  variant="outline"
                  onClick={onPrepareAgain}
                  data-testid="review-reprepare-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" /> Re-prepare
                </Button>
              )}
              {!filed && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={onGoToFile}
                  data-testid="review-go-file-btn"
                >
                  Continue to File <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function DetailTable({ testid, title, columns, rows }) {
  const fmtCell = (val, fmt) => {
    if (val === null || val === undefined) return '—';
    if (fmt === 'currency') return fmtSAR(val);
    return String(val);
  };
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">{title}</div>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r, idx) => (
              <tr key={r.id || idx} className="border-t border-slate-100 hover:bg-indigo-50/40">
                {columns.map((c) => {
                  const v = r[c.key] ?? (c.altKeys || []).map((k) => r[k]).find((x) => x !== undefined);
                  return (
                    <td key={c.key} className={`px-3 py-2 ${c.emphasis ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                      {fmtCell(v, c.format)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 50 && (
        <div className="text-xs text-slate-400 mt-1">
          Showing first 50 of {rows.length} rows
        </div>
      )}
    </div>
  );
}


// ─── File Tab ────────────────────────────────────────────────────────────

/** Trigger a download for the filed VAT return PDF/Excel. */
async function downloadVatFile(returnId, fmt, periodName) {
  try {
    const r = await axiosInstance.get(
      `/vat/returns/${returnId}/export/${fmt}`,
      { responseType: 'blob' });
    const ext  = fmt === 'pdf' ? 'pdf' : 'xlsx';
    const url  = window.URL.createObjectURL(new Blob([r.data]));
    const a    = document.createElement('a');
    a.href = url;
    a.download = `VAT-Return-${periodName || returnId}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`تم تحميل ملف ${fmt.toUpperCase()}`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || `فشل تحميل ${fmt.toUpperCase()}`);
  }
}


function FileTab({ saved, onOpenConfirm }) {
  if (!saved) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500" data-testid="file-empty">
          <Lock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <div className="text-sm">Prepare the return first — there is nothing to file yet.</div>
        </CardContent>
      </Card>
    );
  }

  const filed = saved.status === 'filed';

  if (filed) {
    return (
      <Card>
        <CardContent className="py-8" data-testid="file-already-filed">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-emerald-100 text-emerald-700">
              <CheckCircle2 size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">Filed and immutable</div>
              <div className="text-sm text-slate-600 mt-1">
                VAT return for <strong>{saved.period_name}</strong> was filed on{' '}
                {fmtTime(saved.filed_at)} by <strong>{saved.filed_by_name}</strong>.
              </div>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold">
                <ShieldCheck size={13} /> ZATCA-grade · audit trail recorded
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Output VAT" value={fmtSAR(saved.output_vat?.standard_vat)} />
                <Stat label="Input VAT" value={fmtSAR(saved.input_vat?.standard_vat)} />
                <Stat label="Net Due" value={fmtSAR(saved.net_vat_due)} />
                <Stat label="Direction" value={saved.direction} />
              </div>

              {/* STA-4: Downloadable frozen snapshot (PDF + Excel) */}
              <div className="mt-5 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="download-vat-pdf-btn"
                  onClick={() => downloadVatFile(saved.id, 'pdf',
                                                  saved.period_name)}>
                  <Download size={14} className="mr-1" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="download-vat-excel-btn"
                  onClick={() => downloadVatFile(saved.id, 'excel',
                                                  saved.period_name)}>
                  <Download size={14} className="mr-1" /> Download Excel
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="file-content">
      <Card className="border-amber-200">
        <CardContent className="py-6">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-amber-100 text-amber-700">
              <AlertTriangle size={18} />
            </div>
            <div className="flex-1">
              <div className="font-bold text-slate-900">Ready to file</div>
              <div className="text-sm text-slate-600 mt-1">
                Once filed, this VAT return becomes <strong>immutable</strong>: the snapshot is frozen,
                the period is stamped with <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">vat_filed_at</code>,
                and the action is logged to the governance audit trail (<code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">VAT_RETURN_FILED</code>).
                Subsequent corrections require a credit note workflow rather than a re-file.
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Period" value={saved.period_name} />
                <Stat label="Output VAT" value={fmtSAR(saved.output_vat?.standard_vat)} />
                <Stat label="Input VAT" value={fmtSAR(saved.input_vat?.standard_vat)} />
                <Stat
                  label="Net Due"
                  value={fmtSAR(saved.net_vat_due)}
                  tone={saved.direction === 'pay' ? 'danger' : saved.direction === 'refund' ? 'positive' : 'neutral'}
                />
              </div>
              <div className="mt-5">
                <Button
                  onClick={onOpenConfirm}
                  data-testid="open-file-confirm-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Send className="w-4 h-4 mr-1.5" /> File VAT Return…
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const toneClass = {
    positive: 'text-emerald-700',
    danger:   'text-rose-700',
    neutral:  'text-slate-900',
  }[tone || 'neutral'];
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}


function ConfirmFileDialog({ open, saved, filing, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent data-testid="confirm-file-dialog">
        <DialogHeader>
          <DialogTitle>File VAT return for {saved?.period_name}?</DialogTitle>
          <DialogDescription>
            This action is irreversible. The snapshot is sealed and the period is
            stamped as VAT-closed. You will not be able to re-prepare or edit
            this return after filing.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">Period</span><span className="font-semibold">{saved?.period_name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Net VAT</span><span className="font-semibold">{fmtSAR(saved?.net_vat_due)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Direction</span><span className="font-semibold">{saved?.direction}</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={filing} data-testid="confirm-file-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={filing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-file-submit-btn"
          >
            {filing
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Filing…</>
              : <><ShieldCheck className="w-4 h-4 mr-1.5" /> Yes, file return</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── History Tab ─────────────────────────────────────────────────────────


function HistoryTab({ rows, loading, onJump }) {
  const [expandedId, setExpandedId] = useState(null);
  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading saved VAT returns…
        </CardContent>
      </Card>
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500" data-testid="history-empty">
          <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <div className="text-sm">No VAT returns have been saved yet.</div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="history-content">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History size={16} className="text-indigo-600" />
          Persisted VAT returns
          <Badge variant="outline" className="ml-2 text-xs">{rows.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Period</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Output VAT</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Input VAT</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Net</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Direction</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Filed at</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.draft;
                const isExpanded = expandedId === r.id;
                const desyncEligible = r.status === 'prepared' || r.status === 'filed';
                return (
                  <React.Fragment key={r.id}>
                  <tr className="border-t border-slate-100 hover:bg-indigo-50/40" data-testid={`history-row-${r.period_name}`}>
                    <td className="px-3 py-2 font-semibold text-slate-900">{r.period_name}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badge.color}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtSAR(r.output_vat?.standard_vat)}</td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtSAR(r.input_vat?.standard_vat)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtSAR(r.net_vat_due)}</td>
                    <td className="px-3 py-2 text-slate-700 capitalize">{r.direction}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.filed_at ? fmtTime(r.filed_at) : '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 justify-end">
                        {desyncEligible && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            className="text-xs text-rose-600 hover:text-rose-800 font-medium flex items-center gap-0.5"
                            data-testid={`history-desync-toggle-${r.period_name}`}
                            title="Scan Sub-Ledger ↔ GL VAT desync"
                          >
                            Desync {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onJump(r.period_name)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-0.5"
                          data-testid={`history-open-${r.period_name}`}
                        >
                          Open <ChevronRight size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-t border-slate-100 bg-slate-50/60">
                      <td colSpan={8} className="px-4 py-3">
                        <VATDesyncSection returnId={r.id} filed={r.status === 'filed'} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// ─── Audit Tab ───────────────────────────────────────────────────────────


function AuditTab({ events, loading }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex items-center justify-center text-slate-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading audit stream…
        </CardContent>
      </Card>
    );
  }
  if (!events || events.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-slate-500" data-testid="audit-empty">
          <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <div className="text-sm">No VAT audit events recorded yet.</div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="audit-content">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck size={16} className="text-indigo-600" />
          VAT governance trail
          <Badge variant="outline" className="ml-2 text-xs">{events.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ol className="space-y-2">
          {events.map((ev, idx) => {
            const isFiled = ev.action === 'VAT_RETURN_FILED';
            return (
              <li
                key={idx}
                data-testid={`audit-row-${idx}`}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  isFiled
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className={`p-1.5 rounded ${isFiled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {isFiled ? <ShieldCheck size={14} /> : <Calculator size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2 text-sm">
                    <span className="font-semibold text-slate-900">
                      {isFiled ? 'Return filed' : 'Return prepared'}
                    </span>
                    <span className="text-xs text-slate-500">by {ev.user_name || ev.user_id || 'system'}</span>
                    <span className="text-xs text-slate-400">· {fmtTime(ev.ts || ev.created_at)}</span>
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    Entity: <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200">{ev.entity_id || '—'}</code>
                    {ev.metadata?.period_name && (
                      <>
                        {' '}· Period: <strong>{ev.metadata.period_name}</strong>
                      </>
                    )}
                    {ev.metadata?.net_vat_due !== undefined && (
                      <>
                        {' '}· Net: <strong>{fmtSAR(ev.metadata.net_vat_due)}</strong>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
